<template>
  <vs-card>
    <div class="vx-row pt-5 px-5">
      <div class="vx-col w-full px-8">
        <form method="POST" @submit.prevent="save_changes">
          <div class="vx-row">
            <!-- Title -->
            <div class="vx-col w-full mb-2 -ml-3">
              <vs-input
                icon-pack="feather"
                class="w-full"
                v-model="form.title"
                :label="`${this.$route.params.type} *`"
                placeholder="Book - Pauri 1"
                name="title"
                id="title"
                v-validate="'required|exclude_special_char'"
                :data-vv-as="this.$route.params.type"
              />
              <span class="text-danger text-sm" v-if="errors.has('title')">{{ errors.first('title') }}</span>
            </div>
            <vs-divider class="mx-4"></vs-divider>

            <!-- Accordion Sections -->
            <vs-collapse class="w-full gap-4 grid grid-cols-1 mx-auto">
              <div v-for="(row, rowIndex) in form.slok_n_pauri_fragment" :key="row._id" class="mb-3 brd1">
                <vs-collapse-item class="brd">
                  <div slot="header">
                    <span class="font-semibold text-lg">Pankti {{ rowIndex + 1 }}</span>
                  </div>
                  <transition>
                    <div class="p-6 rounded-md mb-2">
                      <!-- Path Header -->
                      <div class="mb-4 flex gap-4 items-center">
                        <h4 class="text-xl font-semibold">Path</h4>
                        <!-- Audio Controls - Redesigned -->
                        <div class="audio-controls flex gap-2" v-if="row.path.punjabi.id">
                          <!-- Record Button -->
                          <vs-button v-if="!recordingStates[`${row._id}-path-punjabi`]"
                            @mousedown="startRecording(row._id, 'path', 'punjabi')"
                            @mouseup="stopRecording(row._id, 'path', 'punjabi')"
                            @mouseleave="stopRecording(row._id, 'path', 'punjabi')"
                            @touchstart="startRecording(row._id, 'path', 'punjabi')"
                            @touchend="stopRecording(row._id, 'path', 'punjabi')" icon="mic" color="primary"
                            class="hold-to-record rounded-full audio-button">
                          </vs-button>
                          <vs-button v-else @mouseup="stopRecording(row._id, 'path', 'punjabi')"
                            @mouseleave="stopRecording(row._id, 'path', 'punjabi')"
                            @touchend="stopRecording(row._id, 'path', 'punjabi')" icon="stop" color="danger"
                            class="rounded-full audio-button">
                          </vs-button>

                          <!-- Tag Buttons -->
                          <div class="flex gap-2 items-center ml-2">
                            <vs-button type="border" class="tag-pill" @click="openTagModal(row, 'audio')">Tag
                              Audios</vs-button>
                            <vs-button type="border" class="tag-pill" @click="openTagModal(row, 'video')">Tag
                              Videos</vs-button>
                            <vs-button type="border" class="tag-pill" @click="openTagModal(row, 'emotion')">Tag
                              Emotions</vs-button>
                          </div>

                          <!-- Audio Preview -->
                          <div v-if="audioElements[`${row._id}-path-punjabi`] || row.path.punjabi.audioUrl"
                            class="audio-preview flex items-center gap-2">
                            <audio controls :src="audioUrls[`${row._id}-path-punjabi`] || row.path.punjabi.audioUrl"
                              class="audio-player" @pause="handleAudioPause(row._id, 'path', 'punjabi')"
                              @play="handleAudioPlay(row._id, 'path', 'punjabi')"
                              :ref="`audio-${row._id}-path-punjabi`"></audio>
                            <vs-button @click="deleteAudio(row._id, 'path', 'punjabi')" class="ml-2" color="danger"
                              type="gradient" icon-pack="feather" icon="icon-trash-2"></vs-button>
                          </div>
                        </div>
                      </div>

                      <!-- Content Sections -->
                      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <!-- Punjabi -->
                        <div class="w-full">
                          <div class="flex mb-2 gap-2">
                            <strong :for="'path-punjabi-' + row._id">Punjabi * </strong>
                            <vx-tooltip :text="`Copy Id`" v-if="row.path.punjabi.id">
                              <label class="flex text-sm font-medium items-center cursor-pointer" @click="copyToClipboard(row.path.punjabi.id)"> {{ row.path.punjabi.id }}</label>
                            </vx-tooltip>
                          </div>

                          <div class="flex items-center gap-2">
                            <div class="vs-component vs-con-textarea w-full input mb-0 vs-textarea-primary">
                              <textarea
                                placeholder="ਸ੍ਰੀ ਗੁਰੂ ਨਾਨਕ ਦੇਵ ਜੀ ਦੀ ਬਾਣੀ ਦਾ ਅਰਥ ਸਿੱਖਿਆ ਅਤੇ "
                                class="w-full input mb-0 vs-textarea"
                                v-validate="'required|exclude_special_char'"
                                :id="'path-punjabi-' + row._id"
                                :name="'path-punjabi-' + row._id"
                                v-model="row.path.punjabi.content"
                                :rows="3"
                                data-vv-as="Punjabi"
                              ></textarea>
                            </div>
                            <vx-tooltip :text="`Update Punjabi Path`" v-if="row.path.punjabi.id">
                              <vs-button
                                class="vs-con-loading__container mr-2 bg-[#1f4e79]"
                                :id="'create-path-punjabi-' + row._id"
                                :disabled="!row.path.punjabi.content"
                                color="success"
                                type="filled"
                                icon-pack="feather"
                                @click="updateSingleRecord(row._id, row.path.punjabi, 'path-punjabi-' + row._id)"
                                icon="icon-save"
                              ></vs-button>
                            </vx-tooltip>
                          </div>
                          <span class="text-danger text-sm" v-if="errors.has('path-punjabi-' + row._id)">{{ errors.first('path-punjabi-' + row._id) }}</span>
                        </div>

                        <!-- Hindi -->
                        <div class="col-span-1">
                          <div class="flex mb-2 gap-2">
                            <strong :for="'path-hindi-' + row._id">Hindi </strong>
                            <vx-tooltip :text="`Copy Id`" v-if="row.path.hindi.id">
                              <label class="flex text-sm font-medium items-center cursor-pointer" @click="copyToClipboard(row.path.hindi.id)"> {{ row.path.hindi.id }}</label>
                            </vx-tooltip>
                          </div>
                          <div class="flex items-center gap-2">
                            <div class="vs-component vs-con-textarea w-full input mb-0 vs-textarea-primary">
                              <textarea
                                placeholder="ੴ सतिनामु करता पुरखु निर्भउ निर्वैरुअकाल मूरति अजूनी सैभं गुर"
                                class="w-full input mb-0 vs-textarea"
                                v-validate="'exclude_special_char'"
                                :id="'path-hindi-' + row._id"
                                :name="'path-hindi-' + row._id"
                                v-model="row.path.hindi.content"
                                :rows="3"
                                data-vv-as="Hindi"
                              ></textarea>
                            </div>
                            <vx-tooltip :text="`Update Hindi Path`" v-if="row.path.hindi.id">
                              <vs-button
                                class="vs-con-loading__container mr-2 bg-[#1f4e79]"
                                :id="'create-path-hindi-' + row._id"
                                :disabled="!row.path.hindi.content"
                                color="success"
                                type="filled"
                                icon-pack="feather"
                                @click="updateSingleRecord(row._id, row.path.hindi, 'path-hindi-' + row._id)"
                                icon="icon-save"
                              ></vs-button>
                            </vx-tooltip>
                          </div>
                          <span class="text-danger text-sm" v-if="errors.has('path-hindi-' + row._id)">{{ errors.first('path-hindi-' + row._id) }}</span>
                        </div>

                        <!-- English -->
                        <div class="col-span-1">
                          <div class="flex mb-2 gap-2">
                            <strong :for="'path-english-' + row._id">English </strong>
                            <vx-tooltip :text="`Copy Id`" v-if="row.path.english.id">
                              <label class="flex text-sm font-medium items-center cursor-pointer" @click="copyToClipboard(row.path.english.id)"> {{ row.path.english.id }}</label>
                            </vx-tooltip>
                          </div>
                          <div class="flex items-center gap-2">
                            <div class="vs-component vs-con-textarea w-full input mb-0 vs-textarea-primary">
                              <textarea
                                placeholder="ੴ Sat Naam Karta Purakh Nirbhau NirvairAkaal"
                                class="w-full input mb-0 vs-textarea"
                                v-validate="'exclude_special_char'"
                                :id="'path-english-' + row._id"
                                :name="'path-english-' + row._id"
                                v-model="row.path.english.content"
                                :rows="3"
                                data-vv-as="English"
                              ></textarea>
                            </div>
                            <vx-tooltip :text="`Update English Path`" v-if="row.path.english.id">
                              <vs-button
                                class="vs-con-loading__container mr-2 bg-[#1f4e79]"
                                :id="'create-path-english-' + row._id"
                                :disabled="!row.path.english.content"
                                color="success"
                                type="filled"
                                icon-pack="feather"
                                @click="updateSingleRecord(row._id, row.path.english, 'path-english-' + row._id)"
                                icon="icon-save"
                              ></vs-button>
                            </vx-tooltip>
                          </div>
                          <span class="text-danger text-sm" v-if="errors.has('path-english-' + row._id)">{{ errors.first('path-english-' + row._id) }}</span>
                        </div>
                      </div>

                      <vs-divider class="my-4"></vs-divider>

                      <!-- transliteration Section -->
                      <div class="mb-4">
                        <h4 class="text-xl font-semibold">Transliteration</h4>
                      </div>

                      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <!-- Punjabi -->
                        <div class="col-span-1">
                          <div class="flex mb-2 gap-2">
                            <strong :for="'transliteration-punjabi-' + row._id">Punjabi </strong>
                            <vx-tooltip :text="`Copy Id`" v-if="row.transliteration.punjabi.id">
                              <label class="flex text-sm font-medium items-center cursor-pointer" @click="copyToClipboard(row.transliteration.punjabi.id)"> {{ row.transliteration.punjabi.id }}</label>
                            </vx-tooltip>
                          </div>
                          <div class="flex items-center gap-2">
                            <div class="vs-component vs-con-textarea w-full input mb-0 vs-textarea-primary">
                              <textarea
                                placeholder="ਸ੍ਰੀ ਗੁਰੂ ਨਾਨਕ ਦੇਵ ਜੀ ਦੀ ਬਾਣੀ ਦਾ ਅਰਥ ਸਿੱਖਿਆ ਅਤੇ"
                                class="w-full input mb-0 vs-textarea"
                                v-validate="'exclude_special_char'"
                                :id="'transliteration-punjabi-' + row._id"
                                :name="'transliteration-punjabi-' + row._id"
                                v-model="row.transliteration.punjabi.content"
                                :rows="3"
                                data-vv-as="Punjabi"
                              ></textarea>
                            </div>
                            <vx-tooltip :text="`Update Punjabi Transliteration`" v-if="row.transliteration.punjabi.id">
                              <vs-button
                                class="vs-con-loading__container mr-2 bg-[#1f4e79]"
                                :id="'create-transliteration-punjabi-' + row._id"
                                :disabled="!row.transliteration.punjabi.content"
                                color="success"
                                type="filled"
                                icon-pack="feather"
                                @click="updateSingleRecord(row._id, row.transliteration.punjabi, 'transliteration-punjabi-' + row._id)"
                                icon="icon-save"
                              ></vs-button>
                            </vx-tooltip>
                          </div>
                          <span class="text-danger text-sm" v-if="errors.has('transliteration-punjabi-' + row._id)">{{ errors.first('transliteration-punjabi-' + row._id) }}</span>
                        </div>

                        <!-- Hindi -->
                        <div class="col-span-1">
                          <div class="flex mb-2 gap-2">
                            <strong :for="'transliteration-hindi-' + row._id">Hindi </strong>
                            <vx-tooltip :text="`Copy Id`" v-if="row.transliteration.hindi.id">
                              <label class="flex text-sm font-medium items-center cursor-pointer" @click="copyToClipboard(row.transliteration.hindi.id)"> {{ row.transliteration.hindi.id }}</label>
                            </vx-tooltip>
                          </div>
                          <div class="flex items-center gap-2">
                            <div class="vs-component vs-con-textarea w-full input mb-0 vs-textarea-primary">
                              <textarea
                                placeholder="श्री गुरु नानक देव जी की वाणी का अर्थ जीवन की गहराई"
                                class="w-full input mb-0 vs-textarea"
                                v-validate="'exclude_special_char'"
                                :id="'transliteration-hindi-' + row._id"
                                :name="'transliteration-hindi-' + row._id"
                                v-model="row.transliteration.hindi.content"
                                :rows="3"
                                data-vv-as="Hindi"
                              ></textarea>
                            </div>
                            <vx-tooltip :text="`Update Hindi Transliteration`" v-if="row.transliteration.hindi.id">
                              <vs-button
                                class="vs-con-loading__container mr-2 bg-[#1f4e79]"
                                :id="'create-transliteration-hindi-' + row._id"
                                :disabled="!row.transliteration.hindi.content"
                                color="success"
                                type="filled"
                                icon-pack="feather"
                                @click="updateSingleRecord(row._id, row.transliteration.hindi, 'transliteration-hindi-' + row._id)"
                                icon="icon-save"
                              ></vs-button>
                            </vx-tooltip>
                          </div>
                          <span class="text-danger text-sm" v-if="errors.has('transliteration-hindi-' + row._id)">{{ errors.first('transliteration-hindi-' + row._id) }}</span>
                        </div>

                        <!-- English -->
                        <div class="col-span-1">
                          <div class="flex mb-2 gap-2">
                            <strong :for="'transliteration-english-' + row._id">English </strong>
                            <vx-tooltip :text="`Copy Id`" v-if="row.transliteration.english.id">
                              <label class="flex text-sm font-medium items-center cursor-pointer" @click="copyToClipboard(row.transliteration.english.id)"> {{ row.transliteration.english.id }}</label>
                            </vx-tooltip>
                          </div>
                          <div class="flex items-center gap-2">
                            <div class="vs-component vs-con-textarea w-full input mb-0 vs-textarea-primary">
                              <textarea
                                placeholder="The meaning of Sri Guru Nanak Dev Ji's Bani "
                                class="w-full input mb-0 vs-textarea"
                                v-validate="'exclude_special_char'"
                                :id="'transliteration-english-' + row._id"
                                :name="'transliteration-english-' + row._id"
                                v-model="row.transliteration.english.content"
                                :rows="3"
                                data-vv-as="English"
                              ></textarea>
                            </div>
                            <vx-tooltip :text="`Update English Transliteration`" v-if="row.transliteration.english.id">
                              <vs-button
                                class="vs-con-loading__container mr-2 bg-[#1f4e79]"
                                :id="'create-transliteration-english-' + row._id"
                                :disabled="!row.transliteration.english.content"
                                color="success"
                                type="filled"
                                icon-pack="feather"
                                @click="updateSingleRecord(row._id, row.transliteration.english, 'transliteration-english-' + row._id)"
                                icon="icon-save"
                              ></vs-button>
                            </vx-tooltip>
                          </div>
                          <span class="text-danger text-sm" v-if="errors.has('transliteration-english-' + row._id)">{{ errors.first('transliteration-english-' + row._id) }}</span>
                        </div>
                      </div>
                      <vs-divider class="my-4"></vs-divider>
                      <!-- Vyakhya Section -->
                      <div class="mb-4">
                        <h4 class="text-xl font-semibold">Vyakhya</h4>
                      </div>

                      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <!-- Punjabi -->
                        <div class="col-span-1">
                          <div class="flex mb-2 gap-2">
                            <strong :for="'vyakhya-punjabi-' + row._id">Punjabi </strong>
                            <vx-tooltip :text="`Copy Id`" v-if="row.vyakhya.punjabi.id">
                              <label class="flex text-sm font-medium items-center cursor-pointer" @click="copyToClipboard(row.vyakhya.punjabi.id)"> {{ row.vyakhya.punjabi.id }}</label>
                            </vx-tooltip>
                          </div>
                          <div class="flex items-center gap-2">
                            <div class="vs-component vs-con-textarea w-full input mb-0 vs-textarea-primary">
                              <textarea
                                placeholder="ਸ੍ਰੀ ਗੁਰੂ ਨਾਨਕ ਦੇਵ ਜੀ ਦੀ ਬਾਣੀ ਦਾ ਅਰੰਭ ਉੱਥੇ ਕਰ ਰਹੇ ਹਨ ਜਿੱਥੇ ਰੱਬ  "
                                class="w-full input mb-0 vs-textarea"
                                v-validate="'exclude_special_char'"
                                :id="'vyakhya-punjabi-' + row._id"
                                :name="'vyakhya-punjabi-' + row._id"
                                v-model="row.vyakhya.punjabi.content"
                                :rows="3"
                                data-vv-as="Punjabi"
                              ></textarea>
                            </div>
                            <vx-tooltip :text="`Update Punjabi Vyakhya`" v-if="row.vyakhya.punjabi.id">
                              <vs-button
                                class="vs-con-loading__container mr-2 bg-[#1f4e79]"
                                :id="'create-vyakhya-punjabi-' + row._id"
                                :disabled="!row.vyakhya.punjabi.content"
                                color="success"
                                type="filled"
                                icon-pack="feather"
                                @click="updateSingleRecord(row._id, row.vyakhya.punjabi, 'vyakhya-punjabi-' + row._id)"
                                icon="icon-save"
                              ></vs-button>
                            </vx-tooltip>
                          </div>
                          <span class="text-danger text-sm" v-if="errors.has('vyakhya-punjabi-' + row._id)">{{ errors.first('vyakhya-punjabi-' + row._id) }}</span>

                          <!-- Vyakhya Punjabi Audio Controls -->
                          <div class="audio-controls flex gap-2" v-if="row.vyakhya.punjabi.id">
                            <vs-button color="primary" class="rounded-full p-0 ai-button" @click="openAIModal(row, 'pu')"> AI </vs-button>
                            <vs-button
                              v-if="!recordingStates[`${row._id}-vyakhya-punjabi`]"
                              @mousedown="startRecording(row._id, 'vyakhya', 'punjabi')"
                              @mouseup="stopRecording(row._id, 'vyakhya', 'punjabi')"
                              @mouseleave="stopRecording(row._id, 'vyakhya', 'punjabi')"
                              @touchstart="startRecording(row._id, 'vyakhya', 'punjabi')"
                              @touchend="stopRecording(row._id, 'vyakhya', 'punjabi')"
                              icon="mic"
                              color="primary"
                              class="hold-to-record rounded-full audio-button"
                            >
                            </vs-button>
                            <vs-button
                              v-else
                              @mouseup="stopRecording(row._id, 'vyakhya', 'punjabi')"
                              @mouseleave="stopRecording(row._id, 'vyakhya', 'punjabi')"
                              @touchend="stopRecording(row._id, 'vyakhya', 'punjabi')"
                              icon="stop"
                              color="danger"
                              class="rounded-full audio-button"
                            >
                            </vs-button>

                            <div v-if="audioElements[`${row._id}-vyakhya-punjabi`] || row.vyakhya.punjabi.audioUrl" class="audio-preview flex items-center gap-2">
                              <audio
                                controls
                                :src="audioUrls[`${row._id}-vyakhya-punjabi`] || row.vyakhya.punjabi.audioUrl"
                                class="audio-player"
                                @pause="handleAudioPause(row._id, 'vyakhya', 'punjabi')"
                                @play="handleAudioPlay(row._id, 'vyakhya', 'punjabi')"
                                :ref="`audio-${row._id}-vyakhya-punjabi`"
                              ></audio>
                              <vs-button @click="deleteAudio(row._id, 'vyakhya', 'punjabi')" class="ml-2" color="danger" type="gradient" icon-pack="feather" icon="icon-trash-2"></vs-button>
                            </div>
                          </div>
                        </div>

                        <!-- Hindi -->
                        <div class="col-span-1">
                          <div class="flex mb-2 gap-2">
                            <strong :for="'vyakhya-hindi-' + row._id">Hindi </strong>
                            <vx-tooltip :text="`Copy Id`" v-if="row.vyakhya.hindi.id">
                              <label class="flex text-sm font-medium items-center cursor-pointer" @click="copyToClipboard(row.vyakhya.hindi.id)"> {{ row.vyakhya.hindi.id }}</label>
                            </vx-tooltip>
                          </div>
                          <div class="flex items-center gap-2">
                            <div class="vs-component vs-con-textarea w-full input mb-0 vs-textarea-primary">
                              <textarea
                                placeholder="श्री गुरु नानक देव जी की वाणी का आरंभ वहाँ से हो रहा है जहाँ "
                                class="w-full input mb-0 vs-textarea"
                                v-validate="'exclude_special_char'"
                                :id="'vyakhya-hindi-' + row._id"
                                :name="'vyakhya-hindi-' + row._id"
                                v-model="row.vyakhya.hindi.content"
                                :rows="3"
                                data-vv-as="Hindi"
                              ></textarea>
                            </div>
                            <vx-tooltip :text="`Update Hindi Vyakhya`" v-if="row.vyakhya.hindi.id">
                              <vs-button
                                class="vs-con-loading__container mr-2 bg-[#1f4e79]"
                                :id="'create-vyakhya-hindi-' + row._id"
                                :disabled="!row.vyakhya.hindi.content"
                                color="success"
                                type="filled"
                                icon-pack="feather"
                                @click="updateSingleRecord(row._id, row.vyakhya.hindi, 'vyakhya-hindi-' + row._id)"
                                icon="icon-save"
                              ></vs-button>
                            </vx-tooltip>
                          </div>
                          <span class="text-danger text-sm" v-if="errors.has('vyakhya-hindi-' + row._id)">{{ errors.first('vyakhya-hindi-' + row._id) }}</span>

                          <!-- Vyakhya Hindi Audio Controls -->
                          <div class="audio-controls flex gap-2" v-if="row.vyakhya.hindi.id">
                            <vs-button color="primary" class="rounded-full p-0 ai-button" @click="openAIModal(row, 'hi')"> AI </vs-button>
                            <vs-button
                              v-if="!recordingStates[`${row._id}-vyakhya-hindi`]"
                              @mousedown="startRecording(row._id, 'vyakhya', 'hindi')"
                              @mouseup="stopRecording(row._id, 'vyakhya', 'hindi')"
                              @mouseleave="stopRecording(row._id, 'vyakhya', 'hindi')"
                              @touchstart="startRecording(row._id, 'vyakhya', 'hindi')"
                              @touchend="stopRecording(row._id, 'vyakhya', 'hindi')"
                              icon="mic"
                              color="primary"
                              class="hold-to-record rounded-full audio-button"
                            >
                            </vs-button>
                            <vs-button
                              v-else
                              @mouseup="stopRecording(row._id, 'vyakhya', 'hindi')"
                              @mouseleave="stopRecording(row._id, 'vyakhya', 'hindi')"
                              @touchend="stopRecording(row._id, 'vyakhya', 'hindi')"
                              icon="stop"
                              color="danger"
                              class="rounded-full audio-button"
                            >
                            </vs-button>

                            <div v-if="audioElements[`${row._id}-vyakhya-hindi`] || row.vyakhya.hindi.audioUrl" class="audio-preview flex items-center gap-2">
                              <audio
                                controls
                                :src="audioUrls[`${row._id}-vyakhya-hindi`] || row.vyakhya.hindi.audioUrl"
                                class="audio-player"
                                @pause="handleAudioPause(row._id, 'vyakhya', 'hindi')"
                                @play="handleAudioPlay(row._id, 'vyakhya', 'hindi')"
                                :ref="`audio-${row._id}-vyakhya-hindi`"
                              ></audio>
                              <vs-button @click="deleteAudio(row._id, 'vyakhya', 'hindi')" class="ml-2" color="danger" type="gradient" icon-pack="feather" icon="icon-trash-2"></vs-button>
                            </div>
                          </div>
                        </div>
                        <!-- English -->
                        <div class="col-span-1">
                          <div class="flex mb-2 gap-2">
                            <strong :for="'vyakhya-english-' + row._id">English </strong>
                            <vx-tooltip :text="`Copy Id`" v-if="row.vyakhya.english.id">
                              <label class="flex text-sm font-medium items-center cursor-pointer" @click="copyToClipboard(row.vyakhya.english.id)"> {{ row.vyakhya.english.id }}</label>
                            </vx-tooltip>
                          </div>
                          <div class="flex items-center gap-2">
                            <div class="vs-component vs-con-textarea w-full input mb-0 vs-textarea-primary">
                              <textarea
                                placeholder="Sri Guru Nanak Dev Ji begins the Bani with a concept"
                                class="w-full input mb-0 vs-textarea"
                                v-validate="'exclude_special_char'"
                                :id="'vyakhya-english-' + row._id"
                                :name="'vyakhya-english-' + row._id"
                                v-model="row.vyakhya.english.content"
                                :rows="3"
                                data-vv-as="English"
                              ></textarea>
                            </div>
                            <vx-tooltip :text="`Update English Vyakhya`" v-if="row.vyakhya.english.id">
                              <vs-button
                                class="vs-con-loading__container mr-2 bg-[#1f4e79]"
                                :id="'create-vyakhya-english-' + row._id"
                                :disabled="!row.vyakhya.english.content"
                                color="success"
                                type="filled"
                                icon-pack="feather"
                                @click="updateSingleRecord(row._id, row.vyakhya.english, 'vyakhya-english-' + row._id)"
                                icon="icon-save"
                              ></vs-button>
                            </vx-tooltip>
                          </div>
                          <span class="text-danger text-sm" v-if="errors.has('vyakhya-english-' + row._id)">{{ errors.first('vyakhya-english-' + row._id) }}</span>
                          <!-- Vyakhya English Audio Controls -->
                          <div class="audio-controls flex gap-2" v-if="row.vyakhya.english.id">
                            <vs-button color="primary" class="rounded-full p-0 ai-button" @click="openAIModal(row, 'en')"> AI </vs-button>
                            <vs-button
                              v-if="!recordingStates[`${row._id}-vyakhya-english`]"
                              @mousedown="startRecording(row._id, 'vyakhya', 'english')"
                              @mouseup="stopRecording(row._id, 'vyakhya', 'english')"
                              @mouseleave="stopRecording(row._id, 'vyakhya', 'english')"
                              @touchstart="startRecording(row._id, 'vyakhya', 'english')"
                              @touchend="stopRecording(row._id, 'vyakhya', 'english')"
                              icon="mic"
                              color="primary"
                              class="hold-to-record rounded-full audio-button"
                            >
                            </vs-button>
                            <vs-button
                              v-else
                              @mouseup="stopRecording(row._id, 'vyakhya', 'english')"
                              @mouseleave="stopRecording(row._id, 'vyakhya', 'english')"
                              @touchend="stopRecording(row._id, 'vyakhya', 'english')"
                              icon="stop"
                              color="danger"
                              class="rounded-full audio-button"
                            >
                            </vs-button>

                            <div v-if="audioElements[`${row._id}-vyakhya-english`] || row.vyakhya.english.audioUrl" class="audio-preview flex items-center gap-2">
                              <audio
                                controls
                                :src="audioUrls[`${row._id}-vyakhya-english`] || row.vyakhya.english.audioUrl"
                                class="audio-player"
                                @pause="handleAudioPause(row._id, 'vyakhya', 'english')"
                                @play="handleAudioPlay(row._id, 'vyakhya', 'english')"
                                :ref="`audio-${row._id}-vyakhya-english`"
                              ></audio>
                              <vs-button @click="deleteAudio(row._id, 'vyakhya', 'english')" class="ml-2" color="danger" type="gradient" icon-pack="feather" icon="icon-trash-2"></vs-button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </transition>
                  <vs-button icon="trash" icon-pack="feather" @click="deleteRow(row._id, rowIndex)" color="danger" class="mb-3 mt-3">Delete Pankti</vs-button>
                </vs-collapse-item>
              </div>
            </vs-collapse>

            <!-- Add Path Section Button -->
            <div class="flex justify-start w-full">
              <vs-button icon="add" icon-pack="feather" @click="addRow">Add Pankti</vs-button>
            </div>
          </div>
          <!-- Save & Reset Button -->
          <div class="vx-row pt-5 px-5 text-center">
            <div class="vx-col w-full">
              <!-- BUTTON -->
              <div class="items-center">
                <vs-button class="vs-con-loading__container mr-2 bg-[#1f4e79]" id="create" @click="save_changes">
                  {{ $route.params.pauriId ? 'Update' : 'Add' }}
                </vs-button>
                <vs-button color="danger" @click="navigateList">Cancel</vs-button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
    <vs-popup class="ai-modal" title="Generate Vyakhya & Audio" :active.sync="showAIPanktiModal">
      <div class="ai-modal-content">
        <div class="flex gap-2 items-center">
          <vs-input label="Pankti *" v-model="aiPanktiText" class="w-3/4" placeholder="Enter text to generate vyakhya ..." />
          <vs-button color="primary" class="mt-8" @click="aiVyakhyaTextGenerate" :disabled="aiLoading || !aiPanktiText"> Generate Vyakhya </vs-button>
        </div>
        <div class="flex gap-2 items-center">
          <div class="w-3/4">
            <label class="vs-input--label block"> Vyakhya * </label>
            <vs-textarea v-model="aiVyakhyaText" placeholder="Enter text to generate audio..." />
          </div>
          <div v-if="aiVyakhyaLanguage !== 'pu'">
            <vs-button color="primary" @click="aiVyakhyaAudioGenerate" :disabled="aiLoading || !aiVyakhyaText"> Generate Audio </vs-button>
          </div>
        </div>

        <!-- Audio Preview Section -->
        <div v-if="aiAudioUrl || aiVyakhyaText" class="audio-preview flex items-center gap-2 mt-4">
          <audio controls :src="aiAudioUrl" v-if="aiAudioUrl"></audio>
          <div v-if="audioGenerateLoading" class="loading-container">
            <div class="loading-spinner"></div>
            <span>Generating Audio...</span>
          </div>
          <vs-button @click="applyAIAudio" color="success" class="ml-2" :disabled="aiLoading"> Save </vs-button>
          <!-- <vs-button @click="clearAIAudio" color="danger" class="ml-2"> Remove </vs-button> -->
        </div>
      </div>
    </vs-popup>

    <!-- Tagging Modals -->
    <!-- Audio Tag Modal -->
    <vs-popup class="tag-modal" title="Tag Audios" :active.sync="showAudioTagModal">
      <div class="p-4" v-if="showAudioTagModal">
        <div class="vx-row mb-4">
          <div class="vx-col w-full">
            <label class="vs-input--label">Select Audios</label>
            <select-2 class="w-full" name="tag-audios" placeholder="Select Audios" v-model="selectedAudios"
              @input="(item) => (selectedAudios = item && item.value)" autocomplete :ssr="true" :multiple="true"
              action="audio/get" />
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <vs-button color="danger" type="flat" @click="showAudioTagModal = false">Cancel</vs-button>
          <vs-button color="primary" @click="saveTag('audio')">Save Tags</vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Tag Video Modal -->
    <vs-popup class="tag-modal" title="Tag Videos" :active.sync="showVideoTagModal">
      <div class="p-4" v-if="showVideoTagModal">
        <div class="vx-row mb-4">
          <div class="vx-col w-full">
            <label class="vs-input--label">Select Videos</label>
            <select-2 class="w-full" name="tag-videos" placeholder="Select Videos" v-model="selectedVideos"
              @input="(item) => (selectedVideos = item && item.value)" autocomplete :ssr="true" :multiple="true"
              action="video/get" />
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <vs-button color="danger" type="flat" @click="showVideoTagModal = false">Cancel</vs-button>
          <vs-button color="primary" @click="saveTag('video')">Save Tags</vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Tag Emotions Modal -->
    <vs-popup class="tag-modal emotions-modal" title="Tag Emotions" :active.sync="showEmotionsTagModal">
      <div class="p-4" v-if="showEmotionsTagModal">
        <div class="emotions-grid">
          <div v-for="emotion in emotionsList" :key="emotion.value" class="emotion-card"
            :class="{ 'emotion-card--selected': selectedEmotions.includes(emotion.value) }"
            @click="toggleEmotion(emotion.value)">
            <div class="emotion-card__check">
              <feather-icon v-if="selectedEmotions.includes(emotion.value)" icon="CheckIcon"
                svgClasses="h-4 w-4 text-white" />
            </div>
            <div class="emotion-card__label">{{ emotion.label }}</div>
            <div class="emotion-card__emoji">{{ emotion.emoji }}</div>
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-6">
          <vs-button color="danger" type="flat" @click="showEmotionsTagModal = false">Cancel</vs-button>
          <vs-button color="primary" @click="saveTag('emotion')">Save Tags</vs-button>
        </div>
      </div>
    </vs-popup>
  </vs-card>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import RecordRTC from 'recordrtc'
import Select2 from '@/components/custom/form-elements/Select2.vue'

export default {
  name: 'AddUpdate',

  components: {
    Select2
  },

  data() {
    return {
      audioStreams: {},
      languageList: {
        pu: 'punjabi',
        hi: 'hindi',
        en: 'english'
      },
      showAIPanktiModal: false,
      aiPanktiRowId: null,
      aiPanktiText: '',
      aiVyakhyaText: '',
      aiVyakhyaLanguage: '',
      aiLoading: false,
      aiAudioBlob: null,
      aiAudioUrl: null,
      aiAudioElement: null,

      // ... existing data properties ...
      audioRecorders: {}, // Track recorder instances by row and type
      audioBlobs: {}, // Store blobs by row and type
      audioUrls: {}, // Store URLs by row and type
      recordingStates: {}, // Track recording state by row and type
      audioElements: {}, // Store audio elements by row and type

      updatedContentLine: null,
      openSections: [], // Open the first section by default
      apiData: [],
      form: {
        slok_n_pauri_fragment: [],
        title: '',
        type: null,
        book: this.$route.params.id,
        ashtapadi: this.$route.params.ashtapadiId,
        snpId: null
      },
      loading: false, // Add a loading flag to prevent multiple API calls,
      breadcrumb: [],
      // Tagging State
      showAudioTagModal: false,
      showVideoTagModal: false,
      showEmotionsTagModal: false,
      tagRowId: null,
      tagSearchQuery: '',
      selectedAudios: [],
      selectedVideos: [],
      selectedEmotions: [],
      rowTags: {},
      emotionsList: [
        { value: 'happy', label: 'Happy', emoji: '😊' },
        { value: 'sad', label: 'Sad', emoji: '😢' },
        { value: 'fear', label: 'Fear', emoji: '😱' },
        { value: 'anger', label: 'Anger', emoji: '😠' },
        { value: 'surprised', label: 'Surprised', emoji: '😐' },
        { value: 'guilt', label: 'Guilt', emoji: '😥' },
        { value: 'confused', label: 'Confused', emoji: '🤔' },
        { value: 'in_love', label: 'In Love', emoji: '🥰' }
      ],
    }
  },

  computed: {
    ...mapState('pauri', ['createLoading', 'pauriRecord']),
    ...mapState('ai', ['audioGenerateLoading']),
    validateForm() {
      return !this.errors.any()
    }
  },

  methods: {
    ...mapActions('pauri', {
      getPauriDetails: 'getPauriDetails',
      updateSingleLine: 'updateSingleLine',
      update: 'update',
      deleteAudioAction: 'deleteAudio',
      fragmentUpdate: 'fragmentUpdate'
    }),

    ...mapActions('ai', {
      aiVyakhyaTextGenerateAPI: 'aiVyakhyaTextGenerate',
      aiVyakhyaAudioGenerateAPI: 'aiVyakhyaAudioGenerate'
    }),

    async deleteAudio(id, sectionId, language) {
      const row = this.form.slok_n_pauri_fragment.find((item) => item._id === id)
      const record = row[sectionId][language]

      this.$vs.dialog({
        type: 'confirm',
        title: 'Confirm Delete',
        text: 'Are you sure you want to delete this audio?',
        accept: async () => {
          try {
            if (!record.audioUrl) {
              // If no ID, just clear any local recording
              this.clearAudio(id, sectionId, language)
              this.$vs.notify({
                title: 'Success',
                text: 'Audio deleted successfully',
                color: 'success',
                position: 'top-center'
              })
              return
            }
            // Use the Vuex action instead of direct API call
            await this.deleteAudioAction(record.id)

            // Clear the audio from the UI
            this.clearAudio(id, sectionId, language)

            // Remove the audio URL from the record
            this.$set(record, 'audioUrl', null)

            this.$vs.notify({
              title: 'Success',
              text: 'Audio deleted successfully',
              color: 'success',
              position: 'top-center'
            })
          } catch (error) {
            this.$vs.notify({
              title: 'Error',
              text: 'Failed to delete audio',
              color: 'danger',
              position: 'top-center'
            })
          }
        },
        acceptText: 'Delete'
      })
    },

    clearAudio(id, sectionId, language) {
      const key = `${id}-${sectionId}-${language}`
      if (this.audioElements[key]) {
        this.audioElements[key].pause()
        URL.revokeObjectURL(this.audioUrls[key])
      }

      // Clean up stream if it exists
      if (this.audioStreams[key]) {
        this.audioStreams[key].getTracks().forEach((track) => track.stop())
      }

      this.$delete(this.audioElements, key)
      this.$delete(this.audioUrls, key)
      this.$delete(this.audioBlobs, key)
      this.$delete(this.recordingStates, key)
      this.$delete(this.audioStreams, key)
    },

    async startRecording(id, sectionId, language) {
      try {
        const key = `${id}-${sectionId}-${language}`
        this.$set(this.recordingStates, key, true)

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

        // Create RecordRTC instance
        const recorder = RecordRTC(stream, {
          type: 'audio',
          mimeType: 'audio/mp3',
          recorderType: RecordRTC.StereoAudioRecorder,
          numberOfAudioChannels: 1,
          desiredSampRate: 16000
        })

        this.$set(this.audioRecorders, key, recorder)
        recorder.startRecording()

        // Store the stream for cleanup
        this.$set(this.audioStreams, key, stream)
      } catch (error) {
        console.error('Error starting recording:', error)
        const key = `${id}-${sectionId}-${language}`
        this.$set(this.recordingStates, key, false)
        this.$vs.notify({
          title: 'Error',
          text: 'Could not access microphone. Please check permissions.',
          color: 'danger',
          position: 'top-center'
        })
      }
    },
    async stopRecording(id, sectionId, language) {
      const key = `${id}-${sectionId}-${language}`
      const recorder = this.audioRecorders[key]

      if (recorder && this.recordingStates[key]) {
        return new Promise((resolve) => {
          recorder.stopRecording(async () => {
            const blob = recorder.getBlob()
            const url = URL.createObjectURL(blob)

            this.$set(this.audioBlobs, key, blob)
            this.$set(this.audioUrls, key, url)
            this.$set(this.audioElements, key, new Audio(url))
            this.$set(this.recordingStates, key, false)

            // Clean up stream
            if (this.audioStreams[key]) {
              this.audioStreams[key].getTracks().forEach((track) => track.stop())
              this.$delete(this.audioStreams, key)
            }

            resolve()
          })
        })
      }
    },

    handleAudioPause(id, section, language) {
      const key = `${id}-${section}-${language}`

      // Only clear if the paused audio is the currently playing one
      if (this.currentlyPlaying && this.currentlyPlaying.key === key) {
        this.currentlyPlaying = null
      }
    },

    handleAudioPlay(id, section, language) {
      const key = `${id}-${section}-${language}`

      // Get the audio element that triggered this event
      const targetAudio = this.$refs[`audio-${key}`] ? this.$refs[`audio-${key}`][0] : null

      if (!targetAudio) return

      // If clicking the same audio that's currently playing
      if (this.currentlyPlaying && this.currentlyPlaying.key === key) {
        if (!targetAudio.paused) {
          targetAudio.pause()
          this.currentlyPlaying = null
        }
        return
      }

      // Stop any currently playing audio
      if (this.currentlyPlaying) {
        this.currentlyPlaying.element.pause()
        this.currentlyPlaying.element.currentTime = 0
      }

      // Play the new audio
      targetAudio.play()
      this.currentlyPlaying = { element: targetAudio, key }

      // Clean up when audio ends naturally
      targetAudio.onended = () => {
        if (this.currentlyPlaying && this.currentlyPlaying.key === key) {
          this.currentlyPlaying = null
        }
      }
    },

    /**
     * Stops all audio playback and resets state
     */
    stopAllAudio() {
      if (this.currentlyPlaying) {
        this.currentlyPlaying.element.pause()
        this.currentlyPlaying.element.currentTime = 0
        this.currentlyPlaying = null
      }
    },

    copyToClipboard(text) {
      // Create a temporary input element to copy the text
      const input = document.createElement('input')
      input.setAttribute('value', text)
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
    },

    // Modified updateSingleRecord to handle audio
    async updateSingleRecord(id, record, fieldName) {
      const isValid = await this.$validator.validate(fieldName)
      if (!isValid) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please fix the error first',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center',
          time: 5000,
          color: 'primary'
        })
        return
      }

      const formData = new FormData()
      formData.append('content', record.content)

      // Add audio if available for this section
      const [section, language] = fieldName.split('-').slice(0, 2)
      const audioKey = `${id}-${section}-${language}`
      if (this.audioBlobs[audioKey]) {
        formData.append('audio', this.audioBlobs[audioKey], `audio-${Date.now()}.mp3`)
      }

      try {
        const { message } = await this.updateSingleLine({
          id: record.id,
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        // Update the audio URL in the row data if we had a new recording
        if (this.audioUrls[audioKey]) {
          this.$set(this.form.slok_n_pauri_fragment.find((item) => item._id === id)[section][language], 'audioUrl', this.audioUrls[audioKey])
        }

        this.$vs.notify({
          title: 'Success',
          text: message,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center',
          time: 10000,
          color: 'success'
        })
        // Clean up
        if (this.audioBlobs[audioKey]) {
          delete this.audioBlobs[audioKey]
          delete this.audioUrls[audioKey]
        }
      } catch ({ message }) {
        this.$vs.notify({
          title: 'Error',
          text: message,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center',
          time: 5000,
          color: 'primary'
        })
      }
    },

    /** Open Tag Modal */
    openTagModal(row, type) {
      this.tagRowId = row._id
      this.tagSearchQuery = ''

      // Restore previously saved tags for this row
      const saved = this.rowTags[row._id] || {}
      this.selectedAudios = saved.audios ? [...saved.audios] : []
      this.selectedVideos = saved.videos ? [...saved.videos] : []
      this.selectedEmotions = saved.emotions ? [...saved.emotions] : []

      if (type === 'audio') {
        this.showAudioTagModal = true
      } else if (type === 'video') {
        this.showVideoTagModal = true
      } else if (type === 'emotion') {
        this.showEmotionsTagModal = true
      }
    },

    /** Save Tag */
    async saveTag(type) {
      // Ensure the rowTags entry exists for this row
      if (!this.rowTags[this.tagRowId]) {
        this.$set(this.rowTags, this.tagRowId, { audios: [], videos: [], emotions: [] })
      }

      // Persist selections into the rowTags map locally
      if (type === 'audio') {
        this.$set(this.rowTags[this.tagRowId], 'audios', Array.isArray(this.selectedAudios) ? [...this.selectedAudios] : [this.selectedAudios])
      } else if (type === 'video') {
        this.$set(this.rowTags[this.tagRowId], 'videos', Array.isArray(this.selectedVideos) ? [...this.selectedVideos] : [this.selectedVideos])
      } else if (type === 'emotion') {
        this.$set(this.rowTags[this.tagRowId], 'emotions', [...this.selectedEmotions])
      }

      try {
        const payload = {
          book: this.form.book || this.$route.params.id,
          fragment: {
            _id: this.tagRowId,
            audios: this.rowTags[this.tagRowId].audios,
            videos: this.rowTags[this.tagRowId].videos,
            emotions: this.rowTags[this.tagRowId].emotions
          }
        }

        await this.fragmentUpdate(payload)

        if (type === 'audio') this.showAudioTagModal = false
        else if (type === 'video') this.showVideoTagModal = false
        else if (type === 'emotion') this.showEmotionsTagModal = false

        this.$vs.notify({
          title: 'Success',
          text: `${type.charAt(0).toUpperCase() + type.slice(1)} tags saved successfully`,
          color: 'success',
          position: 'top-center'
        })
      } catch (error) {
        console.error('Error saving tags:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to save tags to database',
          color: 'danger',
          position: 'top-center'
        })
      }
    },

    /** Toggle Emotion Selection */
    toggleEmotion(value) {
      const idx = this.selectedEmotions.indexOf(value)
      if (idx > -1) {
        this.selectedEmotions.splice(idx, 1)
      } else {
        this.selectedEmotions.push(value)
      }
    },

    /** Toggle Body Scroll when Modals Open/Close */
    toggleBodyScroll() {
      const isAnyOpen =
        this.showAudioTagModal ||
        this.showVideoTagModal ||
        this.showEmotionsTagModal ||
        this.showAIPanktiModal
      document.body.style.overflow = isAnyOpen ? 'hidden' : 'auto'
    },

    transformApiData(apiData) {
      const rowTags = {}
      const mappedData = apiData.map((item) => {
        const row = {
          path: {},
          transliteration: {},
          vyakhya: {},
          _id: item._id
        }

        item.slok_n_pauri_catalog.forEach((catalog) => {
          const type = catalog._id.toLowerCase()

          catalog.data.forEach((entry) => {
            const language = entry.language.toLowerCase()
            const key = `${row._id}-${type}-${language}`

            row[type][language] = {
              content: entry.content,
              id: entry._id,
              audioUrl: entry.audio
            }

            // Preload audio if available
            if (entry.audio) {
              this.$set(this.audioElements, key, new Audio(entry.audio))
              this.$set(this.recordingStates, key, false)
            }
          })
        })

        rowTags[item._id] = {
          audios: item.audios ? item.audios.map(a => a._id || a) : [],
          videos: item.videos ? item.videos.map(v => v._id || v) : [],
          emotions: item.emotions || []
        }
        return row
      })
      this.rowTags = rowTags
      return mappedData
    },

    /** Add New Section */
    addRow() {
      this.form.slok_n_pauri_fragment.push({
        path: {
          punjabi: {
            content: ''
          },
          hindi: {
            content: ''
          },
          english: {
            content: ''
          }
        },
        transliteration: {
          punjabi: {
            content: ''
          },
          hindi: {
            content: ''
          },
          english: {
            content: ''
          }
        },
        vyakhya: {
          punjabi: {
            content: ''
          },
          hindi: {
            content: ''
          },
          english: {
            content: ''
          }
        }
      })
      // Open the newly added section
      this.openSections.push(this.form.slok_n_pauri_fragment.length - 1)
    },
    /** Delete Section */
    deleteRow(id, rowIndex) {
      if (this.form.slok_n_pauri_fragment.length > 1) {
        this.$vs.dialog({
          type: 'confirm',
          color: 'primary',
          title: `Confirm Delete`,
          text: `Are you sure you want to delete?`,
          accept: () => {
            const index = id ? this.form.slok_n_pauri_fragment.findIndex((item) => item._id === id) : rowIndex
            this.form.slok_n_pauri_fragment.splice(index, 1)
            // Remove from openSections if it's currently open
            const openIndex = this.openSections.indexOf(index)
            if (openIndex > -1) {
              this.openSections.splice(openIndex, 1)
            }
            // Adjust open sections to reflect the new indices
            this.openSections = this.openSections.map((i) => (i > index ? i - 1 : i))
          },
          acceptText: 'Delete'
        })
      } else {
        this.$vs.notify({
          title: 'Error',
          text: 'You must have at least one slock pankti',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center',
          time: 5000,
          color: 'primary'
        })
      }
    },

    /** Save that changes / Update Record */
    async save_changes() {
      if (this.form.slok_n_pauri_fragment.length < 1) {
        this.$vs.notify({
          title: 'Error',
          text: 'You must have at least one slock pankti',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center',
          time: 5000,
          color: 'primary'
        })
        return false
      }
      if (!(await this.$validator.validate())) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please fix the error first',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center',
          time: 5000,
          color: 'primary'
        })
        return false
      }
      try {
        const response = await this.update(this.form)
        this.$router.push({
          name: 'ashtapadiPauri',
          params: { id: this.form.book } // Replace `yourIdValue` with the actual value for the id
        })
        // this.$emit('update-data', true)
        this.$vs.notify({
          title: 'Success',
          text: response.message,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center',
          time: 5000,
          color: 'success'
        })
      } catch ({ message }) {
        this.$vs.notify({
          title: 'Error',
          // text: message,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center',
          time: 5000,
          color: 'primary'
        })
      }
    },

    /** Navigate to Pauries List */
    navigateList() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: `Confirm Cancel`,
        text: `Are you sure you want to cancel?`,
        accept: () => {
          this.$router.push(`/book/${this.$route.params.id}/ashtapadi/${this.$route.params.ashtapadiId}/pauri`)
        },
        acceptText: 'Yes',
        cancelText: 'No'
      })
    },

    /** get Details */
    async getDetail() {
      await this.getPauriDetails(this.form.snpId)
    },

    /* ----------------------------- AI Related Code ---------------------------- */
    async aiVyakhyaTextGenerate() {
      if (!this.aiPanktiText) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please enter text to generate vyakhya',
          color: 'primary',
          position: 'top-center'
        })
        return
      }

      this.aiLoading = true
      try {
        // Clear any existing audio
        this.clearAIAudio()

        // Call the API
        const response = await this.aiVyakhyaTextGenerateAPI({
          input: this.aiPanktiText,
          lang: this.aiVyakhyaLanguage // or whatever language you need
        })
        this.aiVyakhyaText = response.data
      } catch (error) {
        console.error('AI generation error:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to generate text',
          color: 'primary',
          position: 'top-center'
        })
      } finally {
        this.aiLoading = false
      }
    },
    async aiVyakhyaAudioGenerate() {
      if (!this.aiPanktiText) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please enter text to generate audio',
          color: 'primary',
          position: 'top-center'
        })
        return
      }

      this.aiLoading = true
      try {
        // Clear any existing audio
        this.clearAIAudio()

        // Call the API
        const response = await this.aiVyakhyaAudioGenerateAPI({
          input: this.aiPanktiText,
          lang: this.aiVyakhyaLanguage // or whatever language you need
        })

        // Create blob from response data (assuming it's binary audio data)
        const blob = new Blob([response.data], { type: 'audio/mp3' })
        const url = URL.createObjectURL(blob)
        // Store the audio data
        this.aiAudioBlob = blob
        this.aiAudioUrl = url
        this.aiAudioElement = new Audio(url)
      } catch (error) {
        console.error('AI generation error:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to generate audio',
          color: 'primary',
          position: 'top-center'
        })
      } finally {
        this.aiLoading = false
      }
    },

    applyAIAudio() {
      if (this.aiAudioBlob || this.aiVyakhyaText) {
        // Find the current row (assuming we want to apply to the first row for now)
        const currentRow = this.form.slok_n_pauri_fragment.find((item) => item._id === this.aiPanktiRowId)
        const language = this.languageList[this.aiVyakhyaLanguage]

        // Update the vyakhya text content if available
        if (this.aiVyakhyaText) {
          this.$set(currentRow.vyakhya[language], 'content', this.aiVyakhyaText)
        }

        // Update the audio if available
        if (this.aiAudioBlob) {
          const key = `${currentRow._id}-vyakhya-${language}`
          this.$set(this.audioBlobs, key, this.aiAudioBlob)
          this.$set(this.audioUrls, key, this.aiAudioUrl)
          this.$set(this.audioElements, key, this.aiAudioElement)
        }

        // Close the modal
        this.showAIPanktiModal = false
        this.clearAIAudio()
        this.$vs.notify({
          title: 'Success',
          text: 'Content applied successfully',
          color: 'success',
          position: 'top-center'
        })
      } else {
        this.$vs.notify({
          title: 'Warning',
          text: 'No content to apply',
          color: 'warning',
          position: 'top-center'
        })
      }
    },

    clearAIAudio() {
      if (this.aiAudioElement) {
        this.aiAudioElement.pause()
        URL.revokeObjectURL(this.aiAudioUrl)
      }
      this.aiAudioBlob = null
      this.aiAudioUrl = null
      this.aiAudioElement = null
    },

    openAIModal(row = '', language = '') {
      this.clearAIAudio()
      switch (language) {
      case 'pu':
        this.aiPanktiText = row.path.punjabi.content
        this.aiVyakhyaText = row.vyakhya.punjabi.content
        break
      case 'hi':
        this.aiPanktiText = row.path.hindi.content
        this.aiVyakhyaText = row.vyakhya.hindi.content
        break
      default:
        this.aiPanktiText = row.path.english.content
        this.aiVyakhyaText = row.vyakhya.english.content
        break
      }
      this.aiPanktiRowId = row._id

      this.aiVyakhyaLanguage = language
      this.showAIPanktiModal = true
    }
  },

  /** On Load */
  async mounted() {
    const id = this.$route.params.id
    const type = this.$route.params.type
    if (id) {
      const parentRes = await this.$store.dispatch('book/show', id)
      const parentData = parentRes.data.data

      const res = await this.$store.dispatch('ashtapadi/show', this.$route.params.ashtapadiId)
      const resData = res.data.data

      // /** Get Seocnd ID for Pass in Detail API */
      this.form.type = this.$route.params.type
      this.form.snpId = this.$route.params.pauriId

      if (this.$route.params.pauriId) {
        // /** Get Details */
        await this.getDetail()
        /** API call for get detail */
        this.form.title = this.pauriRecord && this.pauriRecord.title
        this.apiData = this.pauriRecord && this.pauriRecord.slok_n_pauri_fragment
        /** Pass data for transform Object */
        this.form.slok_n_pauri_fragment = this.transformApiData(this.apiData)

        this.breadcrumb = [
          { title: 'Dashboard', url: '/dashboard' },
          { title: 'Book', url: '/book/list' },
          { title: parentData.title, url: `/book/${id}/ashtapadi` },
          { title: resData.title, url: `/book/${id}/ashtapadi/${this.$route.params.ashtapadiId}/pauri` },
          { title: `Edit ${this.pauriRecord.title}`, active: true }
        ]
      } else {
        this.addRow()

        this.breadcrumb = [
          { title: 'Dashboard', url: '/dashboard' },
          { title: 'Book', url: '/book/list' },
          { title: parentData.title, url: `/book/${id}/ashtapadi` },
          { title: resData.title, url: `/book/${id}/ashtapadi/${this.$route.params.ashtapadiId}/pauri` },
          { title: `Add ${type}`, active: true }
        ]
      }
    }
  },

  /** watch */
  watch: {
    breadcrumb(newvalue) {
      this.$emit('update-breadcrumb', newvalue)
    },
    createLoading() {
      if (this.createLoading) {
        this.$vs.loading({
          container: '#create',
          scale: 0.45
        })
      } else {
        this.$vs.loading.close('#create > .con-vs-loading')
      }
    },
    showAIPanktiModal() {
      this.toggleBodyScroll()
    },
    showAudioTagModal() {
      this.toggleBodyScroll()
    },
    showVideoTagModal() {
      this.toggleBodyScroll()
    },
    showEmotionsTagModal() {
      this.toggleBodyScroll()
    }
  }
}
</script>

<style>
.rotate-180 {
  transform: rotate(180deg);
  transition-duration: 3s;
}

.clr {
  color: #1f4e79;
}

.brd1 {
  border: none !important;
}

.brd {
  background-image: linear-gradient(to left, #5381ac, #215483);
}

/* Add your custom placeholder color here */
textarea::placeholder {
  color: #b0bec5;
  /* Light color for the placeholder */
  font-weight: 200;
}

/* Optional: additional styles */
input,
textarea {
  border: 1px solid rgb(255, 255, 255);
  font-weight: 600;
}

.audio-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
/*
.audio-preview {
  display: flex;
  align-items: center;
  gap: 8px;
} */

.audio-player {
  max-width: 250px;
}

.hold-to-record {
  transition: all 0.2s;
  white-space: nowrap;
}

.hold-to-record:active {
  transform: scale(0.95);
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
}

@media (max-width: 768px) {
  .audio-player {
    max-width: 120px;
  }
}

.audio-button {
  width: 60px !important;
  height: 60px !important;
}

.small-icon-button .vs-button--icon {
  font-size: 1rem !important;
}

.tag-pill {
  padding: 8px 16px !important;
  border-radius: 20px !important;
  font-size: 12px !important;
  height: auto !important;
  white-space: nowrap;
}

.tag-pill:hover {
  background: rgba(var(--vs-primary), 0.1) !important;
}

.ai-button {
  width: 59px !important;
  height: 59px !important;
}

.loading-container {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #3498db;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.tag-modal .vs-popup {
  width: 600px !important;
}

.emotions-modal .vs-popup {
  width: 560px !important;
}

/* Emotions Grid */
.emotions-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.emotion-card {
  position: relative;
  background: #f5f5f5;
  border: 2px solid transparent;
  border-radius: 12px;
  padding: 16px 8px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.emotion-card:hover {
  background: #ebebeb;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.emotion-card--selected {
  background: #f0f4ff;
}

.emotion-card__check {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #ccc;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.emotion-card--selected .emotion-card__check {
  background-color: black;
}

.emotion-card__emoji {
  font-size: 42px;
  line-height: 1;
  margin: 6px 0;
}

.emotion-card__label {
  font-size: 13px;
  font-weight: 600;
  color: black;
  text-align: center;
  margin-top: 20px;
  margin-bottom: 10px;
}

.emotion-card--selected .emotion-card__label {
  color: var(--vs-primary, #5381ac);
}

.ai-button {
  width: 59px !important;
  height: 59px !important;
}

.loading-container {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #3498db;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
